// src/components/MsoAuditEvidence.tsx
//
// ★★ ANNEX A 22 - "Audit evidence": the most recent audit events for one MSO record (or for the whole
// engine), shown where the officer is already looking, with a link into the audit trail filtered to the
// same record.
//
// The MSO routes record every consequential act (a draft created or edited, a test run, an activation, a
// calculation, a refusal) as an audit event. Until now the screens SAID so in a sentence and left the
// officer to find the trail and type a resource id into its filter. This panel reads the same
// `GET /api/audit` the trail does, so it adds no second record: it is the trail, narrowed, in place.
//
// Every state is stated honestly: loading is announced, a failure names itself and offers a retry (it is
// never rendered as "no events"), and an empty list says nothing has been recorded rather than implying a
// fault. The list is capped at the latest 20; the link is how an officer reads the rest.
//
// ⚠ "Who" is the actor's ROLE, because the trail carries a user id and a role and no name, and a raw id
// is not an answer to "who". The id is in the full trail for anyone entitled to open it.
import { ActionBadge, AuditOutcomeBadge, RoleBadge } from './StatusBadge'
import { Link } from 'react-router'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { formatInstant } from '../lib/format'
import { ErrorCard, LoadingCard } from './StateCards'
import type { AuditEvent } from '../lib/types'

export const AUDIT_EVIDENCE_LIMIT = 20

export interface AuditEvidenceScope {
  readonly resourceType: string
  readonly resourceId?: string
  /** What the link into the trail is called, when a panel carries more than one. */
  readonly linkLabel?: string
}

const trailHref = (scope: AuditEvidenceScope): string => {
  const query = new URLSearchParams({ resourceType: scope.resourceType })
  if (scope.resourceId !== undefined) query.set('resourceId', scope.resourceId)
  return `/audit?${query.toString()}`
}


async function loadEvents(scopes: readonly AuditEvidenceScope[]): Promise<readonly AuditEvent[]> {
  const pages = await Promise.all(
    scopes.map((scope) =>
      api.audit({
        limit: AUDIT_EVIDENCE_LIMIT,
        resourceType: scope.resourceType,
        ...(scope.resourceId !== undefined && { resourceId: scope.resourceId }),
      }),
    ),
  )
  // Newest first across every scope, then the latest N of the merged list.
  return pages
    .flatMap((page) => page.events)
    .sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0))
    .slice(0, AUDIT_EVIDENCE_LIMIT)
}

export function MsoAuditEvidence({
  scopes,
  heading = 'Audit evidence',
  intro,
}: {
  readonly scopes: readonly AuditEvidenceScope[]
  readonly heading?: string
  readonly intro?: string
}) {
  const key = scopes.map((s) => `${s.resourceType}:${s.resourceId ?? ''}`).join('|')
  const events = useApi(() => loadEvents(scopes), [key])

  return (
    <section className="pt-card flex flex-col gap-3" aria-label={heading} data-testid="mso-audit-evidence">
      <div>
        <h2 className="text-lg font-semibold mb-1">{heading}</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          {intro ?? `The latest ${AUDIT_EVIDENCE_LIMIT} audit events recorded for this.`}
        </p>
      </div>

      {events.error !== null ? (
        <ErrorCard title="The audit events could not be loaded" message={events.error} {...(events.retryable && { onRetry: events.reload })} />
      ) : events.initialLoading || events.data === undefined ? (
        <LoadingCard label="Loading the audit events" rows={2} />
      ) : events.data.length === 0 ? (
        <p className="text-sm m-0" data-testid="audit-evidence-empty">
          No audit event has been recorded for this yet, or none is visible to your role.
        </p>
      ) : (
        <div className="pt-table-wrap" aria-busy={events.loading}>
          <table className="pt-table">
            <caption className="sr-only">The latest audit events, newest first</caption>
            <thead>
              <tr>
                <th scope="col" data-unsortable="the latest events, newest first, are one fixed window of the trail">
                  When
                </th>
                <th scope="col" data-unsortable="the latest events, newest first, are one fixed window of the trail">
                  Action
                </th>
                <th scope="col" data-unsortable="the latest events, newest first, are one fixed window of the trail">
                  Who
                </th>
                <th scope="col" data-unsortable="the latest events, newest first, are one fixed window of the trail">
                  Outcome
                </th>
              </tr>
            </thead>
            <tbody>
              {events.data.map((event) => (
                <tr key={event.id}>
                  <td className="text-sm">{formatInstant(event.occurredAt)}</td>
                  <td className="text-sm">
                    <ActionBadge action={event.action} />
                  </td>
                  <td className="text-sm"><RoleBadge role={event.actorRole} /></td>
                  <td className="text-sm"><AuditOutcomeBadge outcome={event.outcome} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm m-0 flex flex-wrap gap-x-4 gap-y-1">
        {scopes.map((scope) => (
          <Link key={trailHref(scope)} to={trailHref(scope)} className="pt-link-target">
            {scope.linkLabel ?? 'Open in the audit trail'}
          </Link>
        ))}
      </p>
    </section>
  )
}
